import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import QRCode from 'qrcode';
import { useAuth } from '../contexts/AuthContext';
import AuthService from '../services/auth';
import Loader from './Loader';

function SecuritySettings({ onUserUpdated }) {
  const { login: updateAuthUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [copyNotice, setCopyNotice] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableTwoFactorCode, setDisableTwoFactorCode] = useState('');
  const [showDisableDialog, setShowDisableDialog] = useState(false);

  useEffect(() => {
    document.title = 'Security Settings - Saral Asset Manager';
    loadStatus();
  }, []);

  const syncUserState = (patch) => {
    const currentUser = AuthService.getCurrentUser();
    if (!currentUser) {
      return;
    }

    const updatedUser = { ...currentUser, ...patch };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    updateAuthUser(updatedUser);
    if (onUserUpdated) {
      onUserUpdated(updatedUser);
    }
  };

  const loadStatus = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await axios.get('/api/auth/2fa/status');
      const currentStatus = response.data;
      let nextQrCodeDataUrl = '';

      if (currentStatus.setupInProgress && currentStatus.otpauthUri) {
        nextQrCodeDataUrl = await QRCode.toDataURL(currentStatus.otpauthUri, { width: 240, margin: 1 });
      }

      setStatus(currentStatus);
      setSetup(currentStatus.setupInProgress ? currentStatus : null);
      setQrCodeDataUrl(nextQrCodeDataUrl);
      setBackupCodes([]);
    } catch (statusError) {
      setError(statusError.response?.data?.message || statusError.response?.data || 'Unable to load security settings');
    } finally {
      setLoading(false);
    }
  };

  const startSetup = async () => {
    setActionLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await axios.post('/api/auth/2fa/setup');
      const setupResponse = response.data;
      const nextQrCodeDataUrl = await QRCode.toDataURL(setupResponse.otpauthUri, { width: 240, margin: 1 });

      setSetup(setupResponse);
      setShowSetupDialog(true);
      setQrCodeDataUrl(nextQrCodeDataUrl);
      setStatus((current) => ({
        ...(current || {}),
        setupInProgress: true,
        twoFactorEnabled: false
      }));
    } catch (setupError) {
      setError(setupError.response?.data?.message || setupError.response?.data || 'Unable to start 2FA setup');
    } finally {
      setActionLoading(false);
    }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await axios.post('/api/auth/2fa/confirm', {
        code: verificationCode
      });

      const nextBackupCodes = response.data.backupCodes || [];
      setBackupCodes(nextBackupCodes);
      setMessage(response.data.message || 'Two-factor authentication enabled');
      setVerificationCode('');
      setShowSetupDialog(true);
      setStatus((current) => ({
        ...(current || {}),
        twoFactorEnabled: true,
        setupInProgress: false,
        hasBackupCodes: true,
        backupCodesRemaining: nextBackupCodes.length
      }));
      syncUserState({
        twoFactorEnabled: true,
        twoFactorSetupRequired: false
      });
    } catch (confirmError) {
      setError(confirmError.response?.data?.message || confirmError.response?.data || 'Unable to confirm 2FA');
    } finally {
      setActionLoading(false);
    }
  };

  const regenerateBackupCodes = async () => {
    setActionLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await axios.post('/api/auth/2fa/backup-codes');
      const nextBackupCodes = response.data.backupCodes || [];
      setBackupCodes(nextBackupCodes);
      setMessage(response.data.message || 'Backup codes regenerated');
      setStatus((current) => ({
        ...(current || {}),
        hasBackupCodes: true,
        backupCodesRemaining: nextBackupCodes.length
      }));
    } catch (regenError) {
      setError(regenError.response?.data?.message || regenError.response?.data || 'Unable to regenerate backup codes');
    } finally {
      setActionLoading(false);
    }
  };

  const downloadBackupCodes = () => {
    if (!backupCodes.length) {
      return;
    }

    const fileContents = [
      'Two-Factor Authentication Backup Codes',
      '',
      'Each code can be used once to sign in when your authenticator app is unavailable.',
      '',
      ...backupCodes
    ].join('\n');

    const fileBlob = new Blob([fileContents], { type: 'text/plain;charset=utf-8' });
    const downloadUrl = URL.createObjectURL(fileBlob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = '2fa-backup-codes.txt';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(downloadUrl);
  };

  const copyBackupCodes = async () => {
    if (!backupCodes.length) {
      return;
    }

    try {
      await navigator.clipboard.writeText(backupCodes.join('\n'));
      setCopyNotice('Backup codes copied to clipboard');
      window.setTimeout(() => setCopyNotice(''), 2500);
    } catch (copyError) {
      setError('Unable to copy backup codes to clipboard');
    }
  };

  const disableTwoFactor = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await axios.post('/api/auth/2fa/disable', {
        currentPassword: disablePassword,
        twoFactorCode: disableTwoFactorCode
      });

      setStatus((current) => ({
        ...(current || {}),
        twoFactorEnabled: false,
        setupInProgress: false,
        hasBackupCodes: false,
        backupCodesRemaining: 0
      }));
      setSetup(null);
      setShowSetupDialog(false);
      setQrCodeDataUrl('');
      setVerificationCode('');
      setBackupCodes([]);
      setDisablePassword('');
      setDisableTwoFactorCode('');
      setShowDisableDialog(false);
      setMessage(response.data.message || 'Two-factor authentication disabled');
      syncUserState({
        twoFactorEnabled: false,
        twoFactorSetupRequired: true
      });
    } catch (disableError) {
      setError(disableError.response?.data?.message || disableError.response?.data || 'Unable to disable 2FA');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="container-fluid">
      <div className="page-header">
        <div>
          <h1 className="page-title mb-1">Security Settings</h1>
          <p className="text-muted mb-0">Manage two-factor authentication and recovery codes for your account.</p>
        </div>
        <div className="page-header-actions">
          <Link className="btn btn-outline-secondary" to="/">
            Back to Dashboard
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body py-5">
            <Loader text="Loading security settings..." />
          </div>
        </div>
      ) : (
        <div className="row g-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h5 className="mb-3">Current Status</h5>
                {error && <div className="alert alert-danger">{error}</div>}
                {message && <div className="alert alert-success">{message}</div>}

                <div className="d-flex flex-column gap-2">
                  <div>
                    <span className="text-muted">2FA</span>
                    <div className={`fw-semibold ${status?.twoFactorEnabled ? 'text-success' : 'text-warning'}`}>
                      {status?.twoFactorEnabled ? 'Enabled' : 'Not enabled'}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted">Setup</span>
                    <div className="fw-semibold">
                      {status?.setupInProgress ? 'In progress' : 'Not started'}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted">Backup codes</span>
                    <div className="fw-semibold">
                      {typeof status?.backupCodesRemaining === 'number'
                        ? `${status.backupCodesRemaining} remaining`
                        : 'Not generated'}
                    </div>
                  </div>
                </div>

                <div className="mt-4 d-flex flex-wrap gap-2">
                  {!status?.twoFactorEnabled && !status?.setupInProgress && !setup && (
                    <button className="btn btn-primary" onClick={startSetup} disabled={actionLoading}>
                      {actionLoading ? 'Preparing...' : 'Start 2FA Setup'}
                    </button>
                  )}
                  {!status?.twoFactorEnabled && (status?.setupInProgress || setup) && (
                    <button className="btn btn-primary" onClick={() => setShowSetupDialog(true)} disabled={actionLoading}>
                      Continue 2FA Setup
                    </button>
                  )}
                  {status?.twoFactorEnabled && (
                    <>
                      <button className="btn btn-outline-primary" onClick={regenerateBackupCodes} disabled={actionLoading}>
                        {actionLoading ? 'Working...' : 'Generate New Backup Codes'}
                      </button>
                      <button className="btn btn-outline-danger" onClick={() => setShowDisableDialog(true)} disabled={actionLoading}>
                        Disable 2FA
                      </button>
                    </>
                  )}
                  <button className="btn btn-outline-secondary" onClick={loadStatus} disabled={actionLoading}>
                    Refresh
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSetupDialog && setup && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-shield-alt me-2"></i>Two-Factor Setup
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowSetupDialog(false)}></button>
              </div>
              <div className="modal-body">
                {backupCodes.length > 0 ? (
                  <>
                    <div className="alert alert-warning">
                      Save these backup codes now. Each one works once if your authenticator app is unavailable.
                    </div>
                    <div className="d-flex justify-content-end gap-2 mb-3">
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={copyBackupCodes}>
                        Copy Codes
                      </button>
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={downloadBackupCodes}>
                        Download TXT
                      </button>
                    </div>
                    <div className="row g-2">
                      {backupCodes.map((code) => (
                        <div className="col-md-4" key={code}>
                          <div className="border rounded p-2 text-center fw-semibold bg-light">{code}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 text-muted">
                      Backup codes are ready. Keep them somewhere safe before closing this dialog.
                    </div>
                  </>
                ) : (
                  <div className="row g-4 align-items-start">
                    <div className="col-md-5 text-center">
                      {qrCodeDataUrl ? (
                        <img src={qrCodeDataUrl} alt="2FA QR code" className="img-fluid rounded border bg-white p-2" />
                      ) : (
                        <div className="border rounded bg-light p-5">QR code will appear here</div>
                      )}
                    </div>
                    <div className="col-md-7">
                      <p className="mb-2">
                        Scan the QR code with Google Authenticator, Microsoft Authenticator, or another TOTP app.
                      </p>
                      <div className="alert alert-info py-2">
                        Manual entry key: <strong>{setup.manualEntryKey}</strong>
                      </div>
                      <form onSubmit={confirmSetup}>
                        <label className="form-label">Verification code</label>
                        <input
                          type="text"
                          className="form-control"
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value)}
                          placeholder="Enter the 6-digit code"
                          required
                        />
                        <div className="d-flex gap-2 mt-3">
                          <button type="submit" className="btn btn-primary" disabled={actionLoading}>
                            {actionLoading ? 'Confirming...' : 'Confirm 2FA'}
                          </button>
                          <button type="button" className="btn btn-outline-secondary" onClick={startSetup} disabled={actionLoading}>
                            {actionLoading ? 'Refreshing...' : 'Reset Setup'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowSetupDialog(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {copyNotice && (
        <div
          className="position-fixed top-0 end-0 m-3 alert alert-success shadow-sm"
          style={{ zIndex: 2000, minWidth: '240px' }}
        >
          {copyNotice}
        </div>
      )}

      {showDisableDialog && status?.twoFactorEnabled && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-danger">Disable Two-Factor Authentication</h5>
                <button type="button" className="btn-close" onClick={() => setShowDisableDialog(false)}></button>
              </div>
              <form onSubmit={disableTwoFactor}>
                <div className="modal-body">
                  <div className="alert alert-warning">
                    Enter your password and a fresh 2FA code to confirm this change.
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Current password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={disablePassword}
                      onChange={(e) => setDisablePassword(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">2FA code</label>
                    <input
                      type="text"
                      className="form-control"
                      value={disableTwoFactorCode}
                      onChange={(e) => setDisableTwoFactorCode(e.target.value)}
                      placeholder="6-digit code or backup code"
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowDisableDialog(false)} disabled={actionLoading}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-danger" disabled={actionLoading}>
                    {actionLoading ? 'Disabling...' : 'Confirm Disable'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SecuritySettings;
